/**
 * Shared top chrome for every NON-editor authenticated page (owner ask 2026-07-22 #6: "выглядит как
 * от разных сайтов" — riders-list, profile, and [any future page] used to each hand-roll their own
 * header in its own style). One component, one markup, one stylesheet section
 * (`style.css`'s "#app-header"): brand (linked to the landing page), the current user's identity
 * (email + admin badge), and Logout — used identically by `riders-list-page.ts` and
 * `profile-page.ts`. The editor keeps its own specialized topbar (a real operations toolbar, not
 * page chrome) but no longer renders its own Logout button — see `docs/V2_DESIGN_SYSTEM.md` §6.
 *
 * Self-contained (CLAUDE.md rule #8): owns its own markup + element lookups; a page composes it by
 * calling `renderAppHeaderHtml()` into its own template and `initAppHeader()` once mounted.
 */
import { getCurrentUser, logout } from "../auth/auth-service";
import { navigate } from "../app/router";
import { icon } from "./icons";
import { brandLogoLinkHtml } from "./logo";
import { t } from "../i18n";

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}

/** Markup only — no listeners yet (those are wired by `initAppHeader` once this is in the DOM,
 *  matching every other component in this codebase, e.g. `preset-menu.ts`). */
export function renderAppHeaderHtml(): string {
  const user = getCurrentUser();
  return `
    <header id="app-header">
      ${brandLogoLinkHtml(24)}
      <div id="app-header-user">
        ${user ? `<span class="mono" title="${escapeHtml(user.email).replace(/"/g, "&quot;")}">${escapeHtml(user.email)}</span>` : ""}
        ${user?.isAdmin ? `<a id="link-admin" href="/admin" class="admin-badge" title="${t("appHeader.adminTitle")}">${t("appHeader.adminBadge")}</a>` : ""}
        <button id="btn-logout" type="button" title="${t("appHeader.logoutTitle")}">${icon("logout")}<span>${t("appHeader.logoutLabel")}</span></button>
      </div>
    </header>`;
}

/** Wires the header's own two live controls. Call once, right after the header's HTML lands in the
 *  DOM (i.e. right after `container.innerHTML = ...` in the page that embeds it). */
export function initAppHeader(root: ParentNode): void {
  root.querySelector<HTMLAnchorElement>(".brand-logo-link")?.addEventListener("click", (event) => {
    event.preventDefault();
    navigate({ name: "landing" });
  });
  // The admin badge doubles as the way in to the admin console — an admin already looks there to
  // confirm their status, so it is where they expect the link to be.
  root.querySelector<HTMLAnchorElement>("#link-admin")?.addEventListener("click", (event) => {
    event.preventDefault();
    navigate({ name: "admin" });
  });
  root.querySelector<HTMLButtonElement>("#btn-logout")?.addEventListener("click", () => {
    logout();
    // auth-service.subscribe() in app-shell.ts reacts to the session change and redirects to
    // /login on its own; navigate() here is only for the instant the auth-subscription hasn't
    // fired yet (matches the pre-existing riders-list-page.ts behaviour this replaces).
  });
}
